import { useState, useEffect, useRef } from 'react';
import { getUnreadNotificationCount, subscribeToNotifications } from '../lib/supabase/notifications';
import { supabase } from '../config/supabase';

/**
 * Custom hook to manage notification badge count with real-time updates
 * Similar to useMessageNotifications but for notifications
 */
export const useNotificationBadge = (userId) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const subscriptionRef = useRef(null);

    // Fetch initial unread count
    const fetchUnreadCount = async () => {
        if (!userId) return;
        try {
            const count = await getUnreadNotificationCount(userId);
            setUnreadCount(count);
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    };

    // Subscribe to real-time notification updates
    useEffect(() => {
        if (!userId) {
            setUnreadCount(0);
            return;
        }

        // Fetch initial count
        fetchUnreadCount();

        // Clean up previous subscription if exists
        if (subscriptionRef.current) {
            supabase.removeChannel(subscriptionRef.current);
        }

        // Subscribe to new notifications
        const channelName = `notification-badge-${userId}-${Date.now()}`;
        console.log('Creating notification badge subscription:', channelName);

        const subscription = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    console.log('New notification received:', payload);
                    // Increment unread count
                    setUnreadCount(prev => prev + 1);
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    console.log('Notification updated:', payload);
                    // If notification was marked as read, decrement count
                    if (payload.new.is_read && !payload.old.is_read) {
                        setUnreadCount(prev => Math.max(0, prev - 1));
                    }
                    // If notification was marked as unread, increment count
                    else if (!payload.new.is_read && payload.old.is_read) {
                        setUnreadCount(prev => prev + 1);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    console.log('Notification deleted:', payload);
                    // If deleted notification was unread, decrement count
                    if (!payload.old.is_read) {
                        setUnreadCount(prev => Math.max(0, prev - 1));
                    }
                }
            )
            .subscribe((status) => {
                console.log('Notification badge subscription status:', status);
            });

        subscriptionRef.current = subscription;

        // Cleanup on unmount
        return () => {
            if (subscriptionRef.current) {
                console.log('Cleaning up notification badge subscription');
                supabase.removeChannel(subscriptionRef.current);
                subscriptionRef.current = null;
            }
        };
    }, [userId]);

    // Refresh function to manually update the count
    const refreshCount = async () => {
        await fetchUnreadCount();
    };

    return {
        unreadCount,
        refreshCount
    };
};
